import React, { useState, useEffect, useMemo, useCallback } from 'react';
import SelectOptionTags from '../../routes/CompanySettings/components/Tags/SelectOptionTags/SelectOptionTags';
import { useUserContext } from '../../context/UserContext';
import SearchComponent from '../SearchComponent/SearchComponent';
import { AnimationWrapper } from '../AnimationWrapper/AnimationWrapper';

const SearchComponentWithTags = ({
  onChange,
  alwaysShow = false,
  clearButton = false, // string || boolean. string represents the clear button text
  className,
  inputClassName,
  maxLength = 500,
  searchState = () => { },
  onClose = () => { },
  onFocus,
  onBlur,
  placeholder = null,
}) => {
  const [internalSearchValue, setInternalSearchValue] = useState('');
  const [tagsList, setTagsList] = useState([]);
  const [searchingTag, setSearchingTag] = useState({ text: '', tagPosition: null });

  const [parsedTags, setParsedTags] = useState([]);

  const [showTagsSelector, setShowTagsSelector] = useState(false);

  const { companyTags } = useUserContext();

  const availableTags = useMemo(() => companyTags.filter(tag => !tagsList.some(item => item.id === tag.id)), [companyTags, tagsList]);

  const tagRegex = /#([^\s./\\(){}<>[\]?*!@#$%^&*+=~±`"'§;:|]*)/g;

  const cleanSearchValue = useMemo(
    () => {
      let cleanedSearch = internalSearchValue.trim();

      if (availableTags.length > 0) {
        cleanedSearch = cleanedSearch
          .replace(tagRegex, '')
          .replace(/\s{2,}/g, ' ')
          .trim()
      }

      return cleanedSearch
    },
    [internalSearchValue]
  );

  const handleOnChange = useCallback(
    value => {
      if (!value) {
        setSearchingTag({ text: '', tagPosition: null });
        setShowTagsSelector(false);
        setInternalSearchValue('');
        setTagsList([]);
        return;
      }

      if (companyTags.length > 0) {
        const foundTags = value.match(tagRegex);
        setParsedTags(foundTags);
        if (foundTags) {
          if (value.length > internalSearchValue.length) {
            if (foundTags.length > tagsList.length) {
              setSearchingTag({ text: foundTags[tagsList.length].replace('#', ''), tagPosition: foundTags.length - 1 });
              setShowTagsSelector(true);
              if (foundTags[foundTags.length - 1].length === 1) {
                const afterTagSymbols = value.split('#').reverse()[0];
                setShowTagsSelector(afterTagSymbols.length === 0);
              }
            } else if (foundTags.length === tagsList.length) {
              // handle cases tag edited in the middle of the text
            }
          } else {
            let tagId = null;
            let searchQuery = '';
            foundTags.forEach(name => {
              const tag = tagsList.find(tag => tag.name === name.replace('#', ''));
              if (!tag) {
                searchQuery = name.replace('#', '');
                tagId = tagsList.find(item => item.name.includes(searchQuery))?.id;
              }
            });
            const tagIndex = foundTags.findIndex(tag => tag.includes(searchQuery));
            if (tagsList.length >= foundTags?.length) {
              if (tagId) {
                setSearchingTag({ text: searchQuery, tagPosition: tagIndex });
                setShowTagsSelector(true);
                setTagsList(prevState => prevState.filter(tag => tag.id !== tagId));
              } else {
                // Exclude removed tag from the list
                setTagsList(prevState => prevState.filter(tag => foundTags.find(item => item.includes(tag.name))));
              }
            } else {
              setSearchingTag({ text: searchQuery, tagPosition: tagIndex });
              setShowTagsSelector(true);
            }
          }
        }
      }
      setInternalSearchValue(value);
    },
    [tagsList, internalSearchValue, companyTags]
  );

  const handleTagSelect = useCallback(
    tagId => {
      setShowTagsSelector(false);
      const tag = companyTags.find(tag => tag.id === tagId);
      // Will replace the part of tag will tag full name and rebuild the search string
      const matches = [...internalSearchValue.matchAll(tagRegex)];

      if (matches[searchingTag.tagPosition]) {
        const matchToReplace = matches[searchingTag.tagPosition][0];
        const startIndex = matches[searchingTag.tagPosition].index;
        const endIndex = startIndex + matchToReplace.length;

        // Modify the tag
        const modifiedTag = `#${tag.name}`;

        // Rebuild the original string with the modified tag at the correct position
        const modifiedString = internalSearchValue.slice(0, startIndex) + modifiedTag + internalSearchValue.slice(endIndex);

        setInternalSearchValue(modifiedString);
      } else {
        console.log('No match at the specified index.');
      }

      // Timeout is here to prevent filtering available tags before the options list is closed
      setTimeout(() => {
        setTagsList(prevState => [...prevState, tag]);
        setSearchingTag({ text: '', tagPosition: null });
      }, 250);
    },
    [companyTags, parsedTags, internalSearchValue, searchingTag]
  );

  useEffect(() => {
    onChange(cleanSearchValue);
  }, [cleanSearchValue]);

  useEffect(() => {
    onChange(tagsList);
  }, [tagsList]);

  return (
    <SearchComponent
      searchValue={internalSearchValue}
      onChange={handleOnChange}
      alwaysShow={alwaysShow}
      clearButton={clearButton}
      className={className}
      inputClassName={inputClassName}
      maxLength={maxLength}
      searchState={searchState}
      onClose={onClose}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
    >
      {companyTags.length > 0 && (
        <AnimationWrapper show={showTagsSelector} from={{ opacity: 0 }} to={{ opacity: 1 }} options={{ duration: 100 }}>
          <SelectOptionTags tags={availableTags} onSelect={handleTagSelect} search={searchingTag.text} style={{ top: 0 }} />
        </AnimationWrapper>
      )}
    </SearchComponent>
  );
};

export default SearchComponentWithTags;
